import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  Alert,
  alpha,
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import BlockIcon from '@mui/icons-material/Block';

import { describeManageError, fieldOfError } from '../manage/manage';
import {
  checkTagForm,
  createVariables,
  EMPTY_TAG_FORM,
  formOf,
  ManagedTag,
  normaliseColor,
  TAG_LIMITS,
  TAG_PALETTE,
  TagForm,
  updateVariables,
} from './tagManager';
import { CRM_MANAGER_CREATE_TAG, CRM_UPDATE_TAG } from './tags.queries';

/** A tag as it shows on a contact: its colour, faint behind its name. */
export const TagChip = ({ name, color, size = 'medium' }: { name: string; color: string | null; size?: 'small' | 'medium' }) => (
  <Chip
    size="small"
    label={name}
    title={name}
    sx={{
      height: size === 'small' ? 22 : 26,
      maxWidth: '100%',
      fontSize: size === 'small' ? '0.75rem' : '0.8125rem',
      fontWeight: 600,
      color: color ?? 'text.primary',
      bgcolor: color ? alpha(color, 0.1) : '#f1f5f9',
      border: '1px solid',
      borderColor: color ? alpha(color, 0.35) : 'divider',
    }}
  />
);

const Swatch = ({ color, label, checked, onClick }: { color: string | null; label: string; checked: boolean; onClick: () => void }) => (
  <Tooltip title={label}>
    <ButtonBase
      role="radio"
      aria-checked={checked}
      aria-label={label}
      onClick={onClick}
      sx={{
        width: 30,
        height: 30,
        borderRadius: '50%',
        bgcolor: color ?? '#f1f5f9',
        color: color ? '#fff' : 'text.secondary',
        border: '2px solid',
        borderColor: checked ? 'text.primary' : color ? 'transparent' : 'divider',
        boxShadow: checked ? '0 0 0 2px #fff inset' : 'none',
        '& svg': { fontSize: 16 },
        '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
      }}
    >
      {checked && color && <CheckIcon />}
      {!color && <BlockIcon />}
    </ButtonBase>
  </Tooltip>
);

interface TagEditorDialogProps {
  /** The tag to edit; absent for a new one. */
  tag?: ManagedTag;
  /** The categories in use, offered as the category is typed. */
  categories: Array<string>;
  onClose: () => void;
  /** After the API has made or changed it. */
  onSaved: (tag: ManagedTag, made: boolean) => void;
}

/** Makes a tag, or changes one's name, category, colour and description. Who carries it does not change. */
export const TagEditorDialog = ({ tag, categories, onClose, onSaved }: TagEditorDialogProps) => {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const [form, setForm] = useState<TagForm>(() => (tag ? formOf(tag) : EMPTY_TAG_FORM));
  const [tried, setTried] = useState(false);
  const [refusal, setRefusal] = useState<unknown>();
  const [create, created] = useMutation<{ crmCreateTag: ManagedTag }>(CRM_MANAGER_CREATE_TAG);
  const [update, updated] = useMutation<{ crmUpdateTag: ManagedTag }>(CRM_UPDATE_TAG);
  const running = created.loading || updated.loading;

  const problems = checkTagForm(form);
  const refusedField = refusal === undefined ? undefined : fieldOfError(refusal);
  const refusedText = refusal === undefined ? undefined : describeManageError(refusal, 'tag');
  const errorOf = (field: keyof TagForm) => {
    if (refusedField === field) return refusedText;
    // The colour is checked as it is typed; the rest once a save is tried.
    return tried || field === 'color' ? problems[field] : undefined;
  };

  const set = (field: keyof TagForm) => (value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    if (refusedField === field) setRefusal(undefined);
  };

  const save = async () => {
    setTried(true);
    setRefusal(undefined);
    if (Object.keys(problems).length) return;
    try {
      if (tag) {
        const changes = updateVariables(tag, form);
        if (!Object.keys(changes).length) {
          onClose();
          return;
        }
        const { data } = await update({ variables: { id: tag.id, ...changes } });
        if (data) onSaved(data.crmUpdateTag, false);
      } else {
        const { data } = await create({ variables: createVariables(form) });
        if (data) onSaved(data.crmCreateTag, true);
      }
    } catch (err) {
      setRefusal(err);
    }
  };

  const chosen = normaliseColor(form.color);
  const custom = chosen && !TAG_PALETTE.includes(chosen) ? chosen : null;
  const previewName = form.name.trim().replace(/\s+/g, ' ') || 'New tag';

  return (
    <Dialog
      open
      onClose={running ? undefined : onClose}
      fullScreen={phone}
      fullWidth
      maxWidth="sm"
      aria-labelledby="crm-tag-editor-title"
    >
      <DialogTitle id="crm-tag-editor-title">{tag ? `Edit ${tag.name}` : 'New tag'}</DialogTitle>
      <Box
        component="form"
        noValidate
        onSubmit={(event: React.FormEvent) => {
          event.preventDefault();
          save();
        }}
      >
        <DialogContent dividers sx={{ display: 'grid', gap: 2.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: '#f8fafc',
              border: '1px dashed',
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              On a contact
            </Typography>
            <Box aria-label="Preview" sx={{ minWidth: 0 }}>
              <TagChip name={previewName} color={chosen} />
            </Box>
          </Box>

          <TextField
            label="Name"
            value={form.name}
            onChange={(event) => set('name')(event.target.value)}
            autoFocus={!tag?.isSystem}
            required
            fullWidth
            disabled={running || tag?.isSystem}
            error={Boolean(errorOf('name'))}
            helperText={
              errorOf('name') ??
              (tag?.isSystem
                ? 'A system tag keeps its name: automation looks for it.'
                : 'Unique in any letter case. Renaming changes it on everyone who carries it.')
            }
            inputProps={{ maxLength: TAG_LIMITS.name + 20 }}
          />

          <Autocomplete
            freeSolo
            options={categories}
            inputValue={form.category}
            onInputChange={(_, value) => set('category')(value)}
            disabled={running}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Category"
                error={Boolean(errorOf('category'))}
                helperText={errorOf('category') ?? 'Groups tags, such as status, campaign or source. Optional.'}
              />
            )}
          />

          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }} id="crm-tag-colour">
              Colour
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
              <Box role="radiogroup" aria-labelledby="crm-tag-colour" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Swatch color={null} label="No colour" checked={!form.color.trim()} onClick={() => set('color')('')} />
                {TAG_PALETTE.map((color) => (
                  <Swatch key={color} color={color} label={color} checked={chosen === color} onClick={() => set('color')(color)} />
                ))}
                {custom && <Swatch color={custom} label={custom} checked onClick={() => undefined} />}
              </Box>
              <TextField
                label="Hex"
                placeholder="#1a82e2"
                value={form.color}
                onChange={(event) => set('color')(event.target.value)}
                disabled={running}
                size="small"
                error={Boolean(errorOf('color'))}
                helperText={errorOf('color')}
                sx={{ width: 140 }}
                inputProps={{ 'aria-label': 'Colour as hex', maxLength: 7 }}
              />
            </Box>
          </Box>

          <TextField
            label="Description"
            value={form.description}
            onChange={(event) => set('description')(event.target.value)}
            disabled={running}
            multiline
            minRows={2}
            fullWidth
            error={Boolean(errorOf('description'))}
            helperText={errorOf('description') ?? `What the tag means, for whoever applies it. ${form.description.trim().length}/${TAG_LIMITS.description}`}
          />

          {refusal !== undefined && !refusedField && <Alert severity="error">{refusedText}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={onClose} disabled={running}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={running}
            startIcon={running ? <CircularProgress size={16} color="inherit" aria-label="Working" /> : undefined}
          >
            {tag ? 'Save changes' : 'Create tag'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};
